/*
 * Blooms & Beans — homepage quick add.
 * Buttons: <button data-bnb-quick-add data-variant-id data-title data-url data-image data-price>
 * Opens one native <dialog> (focus trap, Escape and inert background are provided
 * by showModal), collects the same delivery details as the product page, then adds
 * the single real variant to the native cart. Focus returns to the trigger on close.
 */
import { DeliveryDetails, i18n, esc } from '@bnb/delivery';
import { addItems } from '@bnb/cart';

let dialog, details, current, trigger, busy = false;

function cartUrl() {
  return ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart';
}

function build() {
  const t = i18n();
  const q = t.quickAdd || {};
  dialog = document.createElement('dialog');
  dialog.className = 'bnb bnb-dialog';
  dialog.setAttribute('aria-labelledby', 'bnb-qa-title');
  dialog.innerHTML = `
    <form class="bnb-dialog__inner" method="dialog" novalidate>
      <div class="bnb-dialog__head">
        <img alt="" width="72" height="72" data-qa-img>
        <div>
          <h2 class="bnb-dialog__title" id="bnb-qa-title" data-qa-title></h2>
          <p class="bnb-price" data-qa-price></p>
          <a class="bnb-link" data-qa-url href="#"></a>
        </div>
        <button type="button" class="bnb-dialog__close" data-qa-close aria-label="${esc(t.close || 'Close')}">
          <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
        </button>
      </div>
      <p class="bnb-dialog__intro">${esc(q.intro || '')}</p>
      <div data-qa-details></div>
      <p class="bnb-status" data-qa-status role="status" aria-live="polite"></p>
      <div class="bnb-dialog__actions" data-qa-done hidden>
        <a class="bnb-btn bnb-btn--primary" data-qa-cart href="${esc(cartUrl())}">${esc(t.viewCart || 'View cart')}</a>
        <button type="button" class="bnb-btn bnb-btn--secondary" data-qa-close>${esc(t.keepShopping || 'Keep shopping')}</button>
      </div>
      <div class="bnb-dialog__actions" data-qa-actions>
        <button type="submit" class="bnb-btn bnb-btn--primary" data-qa-submit>${esc(q.submit || 'Add to cart')}</button>
        <button type="button" class="bnb-btn bnb-btn--secondary" data-qa-close>${esc(t.cancel || 'Cancel')}</button>
      </div>
    </form>`;
  document.body.appendChild(dialog);
  details = new DeliveryDetails(dialog.querySelector('[data-qa-details]'));
  dialog.querySelectorAll('[data-qa-close]').forEach((b) => b.addEventListener('click', () => dialog.close()));
  dialog.addEventListener('close', () => { trigger?.focus({ preventScroll: true }); });
  dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });
  dialog.querySelector('form').addEventListener('submit', onSubmit);
}

function open(btn) {
  if (!dialog) build();
  trigger = btn;
  current = { id: Number(btn.dataset.variantId), title: btn.dataset.title, url: btn.dataset.url };
  dialog.querySelector('[data-qa-img]').src = btn.dataset.image || '';
  dialog.querySelector('[data-qa-title]').textContent = current.title;
  dialog.querySelector('[data-qa-price]').innerHTML = btn.dataset.priceHtml || '';
  const link = dialog.querySelector('[data-qa-url]');
  link.href = current.url; link.textContent = btn.dataset.viewLabel || 'View product';
  dialog.querySelector('[data-qa-status]').textContent = '';
  dialog.querySelector('[data-qa-status]').className = 'bnb-status';
  dialog.querySelector('[data-qa-done]').hidden = true;
  dialog.querySelector('[data-qa-actions]').hidden = false;
  dialog.querySelector('[data-qa-details]').hidden = false;
  details.fillSlots();
  dialog.showModal();
}

async function onSubmit(e) {
  e.preventDefault();
  if (busy || !current) return;
  if (!details.validate()) return;
  const t = i18n().quickAdd || {};
  const submit = dialog.querySelector('[data-qa-submit]');
  const status = dialog.querySelector('[data-qa-status]');
  busy = true;
  submit.disabled = true; submit.setAttribute('aria-busy', 'true'); submit.textContent = t.adding || 'Adding…';
  const res = await addItems([{ id: current.id, quantity: 1, properties: details.properties() }], `bnb-qa-${current.id}`);
  busy = false;
  submit.disabled = false; submit.removeAttribute('aria-busy'); submit.textContent = t.submit || 'Add to cart';
  if (res.ok) {
    if (document.querySelector('cart-drawer-component[auto-open]')) {
      dialog.close(); // the theme's cart drawer opens itself on the CartAddEvent
    } else {
      status.className = 'bnb-status bnb-status--ok';
      status.textContent = i18n().added || 'Added to your cart';
      dialog.querySelector('[data-qa-details]').hidden = true;
      dialog.querySelector('[data-qa-actions]').hidden = true;
      dialog.querySelector('[data-qa-done]').hidden = false;
      dialog.querySelector('[data-qa-cart]').focus();
    }
  } else {
    status.className = 'bnb-status bnb-status--error';
    status.textContent = res.message ? `${t.error || ''} (${res.message})` : (t.error || 'Something went wrong.');
  }
}

document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-bnb-quick-add]');
  if (!btn) return;
  e.preventDefault();
  open(btn);
});
